import React, { useRef, useState } from "react";
import { Upload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";

// Admin: upload many lesson files at once. File names decide the lesson:
// "M2-L1 Daily Riyaz.mp4" -> lesson m2l1 video, "M2-L1 Daily Riyaz - Action Sheet.pdf" -> lesson m2l1 Action Sheet.
const lessonOf = (name) => {
    const m = /^m(\d+)\s*-\s*l(\d+)\b/i.exec(name.trim());
    return m ? `m${Number(m[1])}l${Number(m[2])}` : null;
};
const kindOf = (name) => (/\.(mp4|m4v|webm|mov)$/i.test(name) ? "video" : /\.pdf$/i.test(name) ? "sheet" : null);

export default function LessonFiles({ course, onDone }) {
    const input = useRef(null);
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const eps = (course?.modules || []).flatMap((m) => m.episodes || []);
    const have = (id, k) => eps.find((e) => e.id === id)?.[k === "video" ? "video_path" : "sheet_path"];
    const missing = eps.filter((e) => !e.video_path || !e.sheet_path).length;

    const start = async (files) => {
        const list = [...files].map((f) => ({ f, name: f.name, lesson: lessonOf(f.name), kind: kindOf(f.name), status: "waiting", pct: 0 }))
            .map((r) => (!r.lesson || !r.kind || !eps.some((e) => e.id === r.lesson) ? { ...r, status: "skipped" } : r));
        setRows(list); setRunning(true);
        for (let i = 0; i < list.length; i++) {
            const r = list[i];
            if (r.status === "skipped") continue;
            const upd = (p) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, ...p } : x)));
            upd({ status: "uploading" });
            try {
                const fd = new FormData(); fd.append("file", r.f);
                const up = await api.post("/admin/upload-course-media", fd, { onUploadProgress: (e) => e.total && upd({ pct: Math.round((e.loaded / e.total) * 100) }) });
                await api.put(`/admin/courses/${course.slug}/episode-media`, { lesson_id: r.lesson, kind: r.kind, path: up.data.path });
                upd({ status: "done", pct: 100 });
            } catch (e) { upd({ status: "error", err: apiError(e.response?.data?.detail) }); }
        }
        setRunning(false); onDone && onDone();
    };

    return (
        <div className="bg-white border border-[#e8e1d4] p-4" data-testid="lesson-files">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h4 className="font-display font-semibold text-sm">Lesson files</h4>
                    <p className="text-xs text-neutral-500 mt-0.5">{eps.length - missing} of {eps.length} lessons have both a video and an Action Sheet. Select all videos and PDFs together; file names like "M2-L1 …" pick the lesson.</p>
                </div>
                <input ref={input} type="file" multiple accept=".mp4,.m4v,.webm,.mov,.pdf" className="hidden" onChange={(e) => e.target.files?.length && start(e.target.files)} data-testid="lesson-files-input" />
                <button disabled={running} onClick={() => input.current?.click()} className="btn-dark !py-2.5 !px-4 text-xs inline-flex items-center gap-2" data-testid="lesson-files-btn">
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {running ? "Uploading…" : "Upload lesson files"}
                </button>
            </div>
            <div className="mt-3 grid grid-cols-11 gap-1" title="Video / Action Sheet per lesson">
                {eps.map((e) => (
                    <div key={e.id} className="text-[10px] text-center py-1 border border-[#eee6d8]" title={e.title}>
                        <div className="font-semibold uppercase">{e.id}</div>
                        <div className={have(e.id, "video") ? "text-green-700" : "text-neutral-300"}>video</div>
                        <div className={have(e.id, "sheet") ? "text-green-700" : "text-neutral-300"}>sheet</div>
                    </div>
                ))}
            </div>
            {rows.length > 0 && (
                <ul className="mt-3 max-h-64 overflow-auto text-xs divide-y divide-[#f1ebe0]">
                    {rows.map((r, i) => (
                        <li key={i} className="py-1.5 flex items-center gap-2">
                            {r.status === "done" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : r.status === "error" ? <AlertCircle className="h-4 w-4 text-red-600" /> : r.status === "uploading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="h-4 w-4" />}
                            <span className="flex-1 truncate">{r.name}</span>
                            <span className="text-neutral-500">{r.status === "skipped" ? "skipped (name not recognised)" : r.status === "uploading" ? `${r.pct}%` : r.status === "error" ? r.err : r.status}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
